import React, { useEffect, useRef, useState } from "react";
import "../../css/Dashboard.scss";
import "../../css/Common.scss";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import {
  pencilIcon,
  plusIcon,
  searchIcon,
  xIcon,
} from "@progress/kendo-svg-icons";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Breadcrumbs from "../../components/Breadcrumbs";
import { TextBox } from "@progress/kendo-react-inputs";
import {
  commonBaseUrl,
  RightAlignedHeaderCell,
} from "../../../services/CommonUtil";
import { Label } from "@progress/kendo-react-labels";
import { process, State } from "@progress/kendo-data-query";

const initialDataState: State = {
  take: 5,
  skip: 0,
  group: [],
};

export const BusinessToBusinessList = () => {
  // Set initial state for the dynamic data
  const baseURL = commonBaseUrl();

  const navigate = useNavigate();
  const hasFetched = useRef(false);

  const [originalData, setOriginalData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filterValue, setFilterValue] = useState("");
  const [dataState, setDataState] = useState<State>(initialDataState);

  // Breadcrums
  const breadCrumbsData = [
    {
      id: "1",
      text: "Booking Management",
      route: "",
    },
    {
      id: "2",
      text: "B2B Service",
      route: "",
    },
  ];

  // Pagination with settings
  const handlePageChange = (pageState: any) => {
    setDataState({ ...dataState, ...pageState });
  };

  const getPageableSettings = () => {
    const length = filteredData.length;
    if (length === 0) {
      return false;
    } else if (length <= 5) {
      return { pageSizes: false, buttonCount: 5 };
    } else if (length <= 10) {
      return { pageSizes: [5, 10], buttonCount: 5 };
    } else if (length <= 20) {
      return { pageSizes: [5, 10, 20], buttonCount: 5 };
    } else {
      return { pageSizes: [5, 10, 20, 50], buttonCount: 5 };
    }
  };

  const enterView = async (item: any) => {
    navigate("/management/b2bservice/createb2bservice", {
      state: { infoId: item.b2BServiceId, bookingStatus: item.bookingStatus },
    });
  };

  useEffect(() => {
    // Prevent second call
    if (hasFetched.current) return;
    hasFetched.current = true;
    getAllB2BApi();
  }, []);

  const getAllB2BApi = async () => {
    try {
      const response = await axios.get(`${baseURL}odata/GetUserB2BServiceList`);
      const b2b = response.data.content;
      setOriginalData(b2b);
      setFilteredData(b2b); // Also initialize searchData with original data
    } catch (err) {
      console.error(err);
    }
  };

  const onFilterChange = (ev: any) => {
    const value = ev.target.value; // Update to use `target.value` from event
    setFilterValue(value);

    if (value === "") {
      setFilteredData(originalData); // Reset to original data when search is empty
    } else {
      const newData = originalData.filter((item: any) => {
        return (
          item.userName?.toLowerCase().includes(value.toLowerCase()) ||
          item.serviceName?.toLowerCase().includes(value.toLowerCase()) ||
          item.bookingId?.toLowerCase().includes(value.toLowerCase())
        );
      });
      setDataState(initialDataState);
      setFilteredData(newData);
    }
  };

  // Reset filter values
  const resetSearch = () => {
    setFilterValue("");
    setFilteredData(originalData); // Reset to original data
  };

  const MultilineTextCell = (props: any) => {
    const { dataItem, field } = props;
    return (
      <td style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}>
        {dataItem[field]}
      </td>
    );
  };

  const handleNavigate = () => {
    navigate("/management/b2bservice/createb2bservice", {
      state: { infoId: undefined, bookingStatus: "" },
    });
  };

  return (
    <div className="overall-view ">
      <div className="space-burger">
        <Breadcrumbs breadCrumbData={breadCrumbsData} />
        <Button
          className="large-button-view"
          onClick={() => handleNavigate()}
          svgIcon={plusIcon}
        >
          Create B2B Service
        </Button>
      </div>
      <hr className="hr-border" />
      <div>
        <div className="filter-container"></div>
        <div className="common-margin view-margin">
          <div className="table-title-view">
            <div className="large-custom-input-container">
              <Label className="list-heading">B2B Service List</Label>
              <TextBox
                className="textBoxcustomLong"
                placeholder="Search using User Name , Booking ID & Service Name"
                name="roomcost"
                value={filterValue}
                onChange={onFilterChange}
                suffix={() => (
                  <>
                    <span className="input-icon">
                      {filterValue ? (
                        <Button svgIcon={xIcon} onClick={resetSearch} />
                      ) : (
                        <Button svgIcon={searchIcon} />
                      )}
                    </span>
                  </>
                )}
              />
            </div>
          </div>
          <div>
            <Grid
              data={process(filteredData, dataState)}
              pageable={getPageableSettings()}
              skip={dataState.skip}
              take={dataState.take}
              total={filteredData.length}
              onPageChange={(e) => setDataState(e.page)}
              onDataStateChange={handlePageChange}
            >
              <GridColumn
                field="SNo"
                title="S.No"
                width={10}
                headerCell={RightAlignedHeaderCell}
                cell={(props) => (
                  <td
                    style={{
                      height: "75px",
                      textAlign: "right",
                      paddingRight: "40px",
                    }}
                  >
                    {props.dataIndex + 1}
                  </td>
                )}
              />
              <GridColumn
                field="userName"
                title="User Name"
                filter="text"
                cell={(props) => (
                  <td
                    style={{
                      whiteSpace: "pre-wrap",
                      wordWrap: "break-word",
                    }}
                  >
                    {props.dataItem.userName}
                  </td>
                )}
              />
              <GridColumn
                field="bookingId"
                title="Booking ID"
                filter="text"
                cell={MultilineTextCell}
              />
              <GridColumn
                field="serviceName"
                title="B2B Service Name"
                filter="text"
                cell={MultilineTextCell}
              />
              <GridColumn
                field="quantity"
                title="Quantity"
                className="customer-name-styles"
                headerCell={RightAlignedHeaderCell}
                cell={(props) => (
                  <td
                    style={{
                      height: "75px",
                      textAlign: "right",
                      paddingRight: "35px",
                    }}
                  >
                    {props.dataItem.quantity
                      ? `${props.dataItem.quantity}`
                      : ""}
                  </td>
                )}
              />
              <GridColumn
                field="amount"
                title="Amount"
                className="customer-name-styles"
                headerCell={RightAlignedHeaderCell}
                cell={(props) => (
                  <td
                    style={{
                      height: "75px",
                      textAlign: "right",
                      paddingRight: "35px",
                    }}
                  >
                    {props.dataItem.amount ? `${props.dataItem.amount}` : ""}
                  </td>
                )}
              />
              <GridColumn
                title="Action"
                className="Data"
                width={"240px"}
                cell={(props) => (
                  <td>
                    <Button
                      className="view-color"
                      type="button"
                      disabled={props.dataItem.isPredefinedTax}
                      onClick={() => {
                        enterView(props.dataItem);
                      }}
                      svgIcon={pencilIcon}
                    />
                  </td>
                )}
              />
            </Grid>
          </div>
        </div>
      </div>
    </div>
  );
};
